import React, { FunctionComponent, Fragment, createRef } from "react";
import styles from "./Banner.module.scss";
import BannerProps from "./BannerProps.interface";

import { useDispatch } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import handleOpenModal from "blocks/helpers/handleOpenModal";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Paragraph from "components/Paragraph/Paragraph";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";

const Banner: FunctionComponent<BannerProps> = (props) => {
    const dispatch = useDispatch();

    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperElementRef = createRef<HTMLDivElement>();
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const imageCrop = getImageCrop(props.content.image?.umbracoFile.crops, undefined, "Square (1:1)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.image?.umbracoWidth, props.content.image?.umbracoHeight);

    const backgroundColor = props.settings.circle1BackgroundColor ? props.settings.circle1BackgroundColor : undefined;
    const overlayColor = props.settings.circle2BackgroundColor ? props.settings.circle2BackgroundColor : undefined;
    const circleColor = props.settings.circle3BackgroundColor ? props.settings.circle3BackgroundColor : undefined;

    const clickScrollHandler = () => {
        if (wrapperElementRef.current) {
            const scrollOverThreshold = wrapperElementRef.current.scrollTop + wrapperElementRef.current.offsetHeight;
            window.scrollTo({
                top: scrollOverThreshold,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    return (
        <section
            className={combineClasses([styles.wrapper, ...wrapperClasses])}
            id={identifier}
            ref={wrapperElementRef}
        >
            <div className={styles.inner}>
                <div className={styles.content}>
                    {/* Tagline */}
                    {props.content.tagline ? <span className={styles.tagline}>{props.content.tagline}</span> : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading
                            className={styles.headline}
                            headlineSize={props.settings.headlineSize}
                            headlineWeight={props.settings.headlineWeight}
                        >
                            {props.content.headline}
                        </DynamicHeading>
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Fragment>
                            <Paragraph
                                className={styles.text}
                                innerHTML={props.content.bodyText}
                            />
                            {/* <Paragraph className={styles.text} innerHTML={"Aenean fringilla est magna, ultricies pellentesque turpis porttitor ut. Suspendisse in risus vel tortor varius auctor. Mauris et porta odio, eget pretium quam. Curabitur at tincidunt arcu, non suscipit dui. Aenean feugiat tempus odio, eget pellentesque elit tempor at. Praesent ullamcorper eros massa, non blandit turpis ultricies bibendum. Mauris scelerisque convallis rutrum. Donec a mauris nulla. Aenean fringilla est magna, ultricies pellentesque turpis porttitor ut. Suspendisse in risus vel tortor varius auctor. Mauris et porta odio, eget pretium quam. Curabitur at tincidunt arcu, non suscipit dui. Aenean feugiat tempus odio, eget pellentesque elit tempor at. Praesent ullamcorper eros massa, non blandit turpis ultricies bibendum. Mauris scelerisque convallis rutrum. Donec a mauris nulla.Aenean fringilla est magna, ultricies pellentesque turpis porttitor ut. Suspendisse in risus vel tortor varius auctor. Mauris et porta odio, eget pretium quam. Curabitur at tincidunt arcu, non suscipit dui. Aenean feugiat tempus odio, eget pellentesque elit tempor at. Praesent ullamcorper eros massa, non blandit turpis ultricies bibendum. Mauris scelerisque convallis rutrum. Donec a mauris nulla."} />
                            <Paragraph className={styles.text} innerHTML={"Aenean fringilla est magna, ultricies pellentesque turpis porttitor ut. Suspendisse in risus vel tortor varius auctor. Mauris et porta odio, eget pretium quam. Curabitur at tincidunt arcu, non suscipit dui. Aenean feugiat tempus odio, eget pellentesque elit tempor at. Praesent ullamcorper eros massa, non blandit turpis ultricies bibendum. Mauris scelerisque convallis rutrum. Donec a mauris nulla. Donec in fringilla magna, suscipit sodales dui. Sed et ligula mauris.Sed ultrices magna et placerat viverra. Maecenas vehicula nulla leo, quis vestibulum nunc tristique eget. Phasellus lobortis lorem nisi, fringilla suscipit felis sagittis sed. Nulla non libero urna. Phasellus suscipit arcu et odio malesuada, ac tincidunt ligula sodales. Praesent nulla libero, condimentum a augue eu, pellentesque interdum nunc. Integer ipsum nisi, blandit iaculis libero vitae, finibus scelerisque diam. Nulla vitae mauris non nisl volutpat ullamcorper. Curabitur pellentesque leo ac magna scelerisque, a fringilla metus aliquet. Phasellus nec finibus ligula. Pellentesque porta est sem, ut tincidunt ante condimentum vitae.Donec id magna accumsan, consectetur tortor eget, venenatis nisi. Nullam pretium non turpis a sagittis. Nullam id euismod ante, eu tincidunt ex. Phasellus quis convallis tellus, eget finibus justo. In dui erat, lacinia nec sem quis, semper sagittis nunc. Duis a mattis dolor, et ultrices elit. Integer in purus nisl.Nam tincidunt quam in magna vulputate, vestibulum gravida sapien viverra. Ut egestas nunc quis nunc faucibus faucibus. Integer a odio lacus. Nunc tempus lectus ex, nec semper quam venenatis a. Mauris at purus quis orci posuere iaculis. Duis sollicitudin fringilla dui et suscipit. Fusce viverra, nibh maximus placerat auctor, sem leo porta tellus, in blandit mi neque non risus. Nunc quis erat sed justo varius pulvinar iaculis id nisl. Mauris ullamcorper tellus quam, nec malesuada lacus eleifend et. Mauris vel finibus sem, quis condimentum leo. Praesent turpis magna, posuere eu lacinia et, scelerisque eget urna. Proin enim ligula, finibus at quam quis, egestas congue ante. Quisque vitae justo est. Mauris sed ante pharetra, varius nibh id, congue mauris. Sed mi lacus, interdum interdum dolor at, auctor tempor metus. Integer dapibus varius vulputate.Nullam vitae felis et ligula consectetur tincidunt eu ut risus. Nunc quis tortor ultricies mauris vulputate tristique. Phasellus consequat est a commodo cursus. Praesent eu nulla ac tellus pellentesque lacinia vitae vitae odio. Aenean vel semper libero. Etiam aliquet nibh lobortis, auctor elit sit amet, fermentum tortor. Curabitur diam nisl, facilisis in pretium at, accumsan a massa. Nullam euismod eros suscipit, blandit diam in, ultricies ligula. Praesent eu diam non mauris efficitur varius vitae ut quam. Proin sodales, sem at tincidunt placerat, metus orci ullamcorper elit, vitae faucibus sapien lacus ac augue. Interdum et malesuada fames ac ante ipsum primis in faucibus. Quisque maximus, nisi sed rhoncus venenatis, massa ipsum malesuada magna, nec interdum mi ante sed lectus."} /> */}
                        </Fragment>
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <Button
                                        key={button.contentUdi}
                                        modal={button.content.modal}
                                        onClick={() => {
                                            button.content.modal ? handleOpenModal(button.content.modal, button.content.stickyLink, dispatch) : undefined;
                                        }}
                                        to={button.content.link}
                                        parameters={button.content.parameters}
                                        anchor={button.content.anchor}
                                        settings={button.settings}
                                    >
                                        {button.content.label}
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>

                {/* Media */}
                <div
                    className={styles.image_wrapper}
                    style={{ backgroundColor: backgroundColor }}
                >
                    {/* Overlay */}
                    {props.content.image ? (
                        <div
                            className={styles.image_overlay}
                            style={{ backgroundColor: overlayColor }}
                        />
                    ) : null}

                    {/* Image */}
                    {props.content.image ? (
                        <ImageViewer
                            src={props.content.image.umbracoFile.src}
                            crop={imageCrop}
                            focalPoint={props.content.image.umbracoFile.focalPoint}
                            alt={props.content.headline}
                            aspectRatioX={imageAspectRatio.aspectRatioX}
                            aspectRatioY={imageAspectRatio.aspectRatioY}
                        />
                    ) : null}
                </div>

                <div
                    className={styles.circle}
                    style={{ backgroundColor: circleColor }}
                />
            </div>

            {/* Aside */}
            <div className={styles.aside}>
                {/* Image */}
                {props.content.aside && props.content.aside.length > 0 && props.content.aside[0].content.asideImage ? (
                    <ImageViewer
                        src={props.content.aside[0].content.asideImage.umbracoFile.src}
                        focalPoint={props.content.aside[0].content.asideImage.umbracoFile.focalPoint}
                        aspectRatioX={1}
                        aspectRatioY={1}
                        alt={props.content.headline}
                        className={styles.aside_image}
                    />
                ) : null}

                {/* Richtext */}
                {props.content.aside && props.content.aside.length > 0 && props.content.aside[0].content.bodyText ? (
                    <Paragraph
                        className={styles.aside_text}
                        innerHTML={props.content.aside[0].content.bodyText}
                    />
                ) : null}

                {/* Links: Headline */}
                {props.content.aside && props.content.aside.length > 0 && props.content.aside[0].content.heading ? (
                    <DynamicHeading className={styles.aside_heading}>{props.content.aside[0].content.heading}</DynamicHeading>
                ) : null}

                {/* Links */}
                {props.content.aside && props.content.aside.length > 0 && props.content.aside[0].content.buttons ? (
                    <div className={styles.aside_buttons}>
                        {props.content.aside[0].content.buttons.map((button: any, index: number) => {
                            return (
                                <Anchor
                                    key={index}
                                    to={button.content.link}
                                    className={styles.aside_button}
                                >
                                    {button.content.label}
                                </Anchor>
                            );
                        })}
                    </div>
                ) : null}
            </div>

            {/* Scroll Button */}
            {props.settings.showAnchorButton ? (
                <button
                    aria-label="Scroll Banner"
                    className={styles.scroll_button}
                    onClick={clickScrollHandler}
                >
                    <IconViewer
                        name="arrow-bottom-left"
                        aspectRatioX={1}
                        aspectRatioY={1}
                    />
                </button>
            ) : null}
        </section>
    );
};

export default Banner;
